// Single source of truth for deriving a human-readable title for a chat session.
//
// Port of the mobile app's src/utils/sessionTitle.ts — keep the two in sync.
// Both apps read the same `user_sessions/{uid}/sessions` docs, so a session
// started on one platform and resumed on the other must be titled identically.
//
// Background: when a session is started by tapping a topic chip (on mobile OR
// the web app), the chip's label (e.g. "General Chat") is sent as the session's
// `initial_answer`. That label is an intake selection, not a meaningful title,
// so it must never be shown as the conversation title.
//
// We handle this two ways, layered:
//   1. New sessions carry `initial_answer_kind` ('topic' | 'recommendation' |
//      'custom') so titling is deterministic with no label lists.
//   2. Legacy sessions are unflagged — for those we fall back to matching the
//      known topic-chip labels below.

// Union of topic-chip labels across the mobile + web apps (all versions).
// Stored lowercased for case-insensitive matching.
const GENERIC_TOPIC_LABELS = new Set([
  // Mobile — ALL_TOPICS (src/components/chat/InitialQuestion.tsx)
  'general chat',
  'relationships',
  'stress',
  'anxiety & worry',
  'personal growth',
  'feeling low',
  'purpose & direction',
  'support session',
  'self-esteem',
  'past trauma',
  'financial stress',
  'family & friends',
  'career & study',
  'grief & loss',
  'identity & sexuality',
  'unsure',
  // Web app — topic chips
  'exploring personal growth',
  'relationship support',
  'looking for purpose or direction',
  'friend & family matters',
  'healing from past trauma',
  'feeling anxious or worried',
  'money & financial worries',
  'feeling down or depressed',
  'struggling with self-esteem',
  'academic & career guidance',
  'coping with grief or loss',
  'exploring sexuality & gender identity',
  'managing or experiencing stress',
]);

/** True if the given text is a generic topic-chip label (not a real title). */
export const isGenericTopicLabel = (text) => {
  if (!text) return false;
  return GENERIC_TOPIC_LABELS.has(text.trim().toLowerCase());
};

const hasSystemMarker = (t) => t.includes('<system') || t.includes('system_instruction');

const truncate = (s, n) => (s.length > n ? s.slice(0, n) + '...' : s);

const TYPE_LABELS = {
  community: 'Community Session',
  symptomGraph: 'Mind Graph',
  scheduled: 'Scheduled Session',
};

/**
 * Derive a display title for a chat/session row. Used by the session picker
 * and the ZoSecure save path so the logic never drifts between them.
 */
export const deriveSessionTitle = (s, maxLen = 50) => {
  const st = s.session_type || '';
  const mode = s.conversation_mode || '';
  const answer = (s.initial_answer || '').trim();

  // Type-based sessions (with enriched topic where available)
  if (st === 'voice') return 'Voice Session';
  if (st === 'psychPlaybook') {
    const m = answer.match(/lesson on ([^(]+)\(/i);
    return m ? `Psych Playbook - ${m[1].trim()}` : 'Psych Playbook';
  }
  if (st === 'deepDive' || mode === 'deep_dive') {
    const m = answer.match(/deep dive.*?on:\s*"([^"]+)"/i);
    return m && m[1] ? `Deep Dive: ${m[1]}` : 'Deep Dive';
  }
  if (st === 'program' || mode === 'program') {
    const m = answer.match(/program.*?topic:\s*"([^"]+)"/i);
    return m && m[1] ? `Program: ${m[1]}` : 'Program';
  }
  if (TYPE_LABELS[st]) return TYPE_LABELS[st];

  // Topic chip (flagged, or matching a known label) → show the topic itself.
  // These are canned labels the user picked from a list, not anything they
  // typed, so there is nothing sensitive in them and "General Chat" tells you
  // far more than "Chat Session". This also stops the picker disagreeing with
  // ZoSecure Conversations, which has always shown the topic.
  // Free-text intake is handled below and stays neutral.
  if (s.initial_answer_kind === 'topic' || isGenericTopicLabel(answer)) {
    return answer ? truncate(answer.charAt(0).toUpperCase() + answer.slice(1), maxLen) : 'Chat Session';
  }

  // Free-text intake ('custom') is personal — never surface it as a title.
  // Prefer the generated summary when one exists, else stay neutral.
  if (s.initial_answer_kind === 'custom') {
    const customSummary = (s.session_summary || s.summary || '').trim();
    if (customSummary && !hasSystemMarker(customSummary)) return truncate(customSummary, maxLen);
    return 'Chat Session';
  }

  // Meaningful recommendation title / legacy free text
  if (answer && !hasSystemMarker(answer)) {
    return truncate(answer.charAt(0).toUpperCase() + answer.slice(1), maxLen);
  }

  // Fallback to any generated summary, else neutral
  const summary = (s.session_summary || s.summary || '').trim();
  if (summary && !hasSystemMarker(summary)) return truncate(summary, maxLen);

  return 'Chat Session';
};
